import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import type { Session, User } from "@supabase/supabase-js";

interface Business {
  id: string;
  name: string;
  slug: string;
  planType?: string;
}

interface SignUpResult {
  error: string | null;
  requiresEmailVerification?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  business: Business | null;
  isLoading: boolean;
  signUp: (email: string, password: string, businessName: string) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session) {
        fetchBusiness(session.access_token);
      } else {
        setIsLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session) {
        await fetchBusiness(session.access_token);
      } else {
        setBusiness(null);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchBusiness(accessToken: string) {
    try {
      const response = await fetch("/api/auth/session", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBusiness(data.business);
      }
    } catch (error) {
      console.error("Error fetching business:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function signUp(email: string, password: string, businessName: string): Promise<SignUpResult> {
    try {
      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, businessName }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { error: data.error?.message || "Failed to create account" };
      }

      // If email verification is required, don't set session (user needs to verify first)
      if (data.requiresEmailVerification) {
        return { error: null, requiresEmailVerification: true };
      }

      // If we got a session back, set it (legacy flow)
      if (data.session) {
        const { data: sessionData } = await supabase.auth.setSession({
          access_token: data.session.accessToken,
          refresh_token: data.session.refreshToken,
        });
        setSession(sessionData.session);
        setUser(sessionData.session?.user ?? null);
        setBusiness(data.business);
      }

      return { error: null };
    } catch (error) {
      return { error: "Network error. Please try again." };
    }
  }

  async function signIn(email: string, password: string) {
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { error: data.error?.message || "Failed to sign in" };
      }

      // Set the session in Supabase client
      if (data.session) {
        const { data: sessionData } = await supabase.auth.setSession({
          access_token: data.session.accessToken,
          refresh_token: data.session.refreshToken,
        });
        setSession(sessionData.session);
        setUser(sessionData.session?.user ?? null);
        setBusiness(data.business);
      }

      return { error: null };
    } catch (error) {
      return { error: "Network error. Please try again." };
    }
  }

  async function signOut() {
    try {
      const accessToken = session?.access_token;
      if (accessToken) {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setBusiness(null);
    }
  }

  async function refreshSession() {
    const { data: { session } } = await supabase.auth.refreshSession();
    setSession(session);
    setUser(session?.user ?? null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        business,
        isLoading,
        signUp,
        signIn,
        signOut,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
